import { Component, h } from 'preact'
import axios from 'axios'
import FeaturedTopicView from './FeaturedTopicView'
import FeaturedProductUpdateView from './FeaturedProductUpdateView'
import { EventListItemView } from '../event/EventListOverview'
import Button from '../../control-components/ui-components/Button'
import { UserData } from '../event/EventAttendees'
import { ProductAreaType } from '../../control-components/product-areas/ProductAreaSelect'

export interface Author {
  id: number
  url: string
  name: string
  avatar: string
}

export interface Post {
  id: number
  author: Author
  content: string
  url: string
  creationDate: string
  relativeCreationDate: string
}

interface IdeationStatus {
  name: string
  bgColor: string
  color: string
}

export interface FeaturedTopic {
  id: number
  title: string
  description: string
  url: string
  type: number
  featuredImage: string
  label: string
  replyCount: number
  views: number
  post: Post
  customClass: string
  likes: number
  hasCurrentUserLiked: boolean
  ideationStatus?: IdeationStatus
  contentType: string
}

export interface FeaturedProductUpdate {
  id: number
  title: string
  url: string
  type: number
  featuredImage: string
  label: string
  replyCount: number
  views: number
  customClass: string
  contentType: 'productUpdate'
  content: string
  author: Author
  createdAt: string
  productAreas: string[]
}

export interface FeaturedEvent {
  title: string
  url: string
  type?: string
  startsAt: number
  endsAt: number
  image?: string
  location?: string
  contentType: string
  isLoggedInUserAttendee: boolean
  attendees?: UserData[]
  attendeeCount?: number
}

interface Props {
  title: string
  topicsFullWidth: number
  topicIds: number[]|Array<{id: number, type: string}>
  buttonLabel?: string
  buttonUrl?: string
  featuredTopics: Array<FeaturedTopic|FeaturedEvent|FeaturedProductUpdate>
  allProductAreas: ProductAreaType[]
}

interface State {
  featuredTopics: Array<FeaturedTopic|FeaturedEvent|FeaturedProductUpdate>
}

export default class FeaturedTopicsWidget extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      featuredTopics: props.featuredTopics || []
    }

    this.addLikeHandler = this.addLikeHandler.bind(this)
  }

  async addLikeHandler (index: number) {
    if (typeof window === 'undefined') return
    if (!window.inSidedData.user.userid) {
      const event = new window.CustomEvent('GuestUserAction')
      return window.dispatchEvent(event)
    }

    const newFeaturedTopics = [...this.state.featuredTopics]
    const topic = newFeaturedTopics[index] as FeaturedTopic
    let { post, id, likes, hasCurrentUserLiked: liked } = topic

    topic.likes = liked ? --likes : ++likes
    topic.hasCurrentUserLiked = !liked

    this.setState({
      featuredTopics: newFeaturedTopics
    })

    const topicHasCurrentUserLiked: string = String(topic.hasCurrentUserLiked)
    const url = `/member/like?like=${topicHasCurrentUserLiked}&tid=${id}`

    const newData = {
      liked: topic.hasCurrentUserLiked,
      postid: post.id
    }

    return await axios.post(url, { ...newData })
  }

  renderEvent ({ title, url, startsAt, endsAt, image, type, location, attendeeCount, attendees, isLoggedInUserAttendee }: FeaturedEvent, customClass: string, index: number, topicsFullWidth: number) {
    const classNameForFeaturedImage: string = image ? 'featured-topic--featured-image' : ''
    const isFullWidth = index < topicsFullWidth || customClass.includes('featured-topic--full-width')
    return (
      <article
        className={`${classNameForFeaturedImage} ${customClass}`}
      >
        <EventListItemView
          title={title}
          startAt={startsAt}
          endAt={endsAt}
          destinationUrl={url}
          image={image}
          type={type}
          location={location}
          isFullWidth={isFullWidth}
          isFeatured
          attendees={attendees}
          attendeeCount={attendeeCount}
          isLoggedInUserAttendee={isLoggedInUserAttendee}
        />
      </article>
    )
  }

  renderProductUpdate (topic: FeaturedProductUpdate, index: number, topicsFullWidth: number, customClass: string, allProductAreas: ProductAreaType[]) {
    const { id, content, label, title, featuredImage, url, replyCount, views, author, createdAt, productAreas } = topic
    return (
      <FeaturedProductUpdateView
        id={id}
        assignedProductAreas={productAreas}
        content={content}
        label={label || ''}
        title={title}
        featuredImage={featuredImage}
        author={author}
        replyCount={replyCount}
        views={views}
        url={url}
        fullWidth={index < topicsFullWidth}
        customClass={customClass}
        index={index}
        allProductAreas={allProductAreas}
        createdAt={createdAt}
      />
    )
  }

  renderTopic ({ likes, type, title, featuredImage, label, replyCount, views, url, post, ideationStatus, hasCurrentUserLiked }: FeaturedTopic, index: number, topicsFullWidth: number, customClass: string) {
    return (
      <FeaturedTopicView
        title={title}
        featuredImage={featuredImage}
        label={label}
        replyCount={replyCount}
        views={views}
        url={url}
        post={post}
        fullWidth={index < topicsFullWidth}
        customClass={customClass}
        type={type}
        likes={likes}
        addLikeHandler={this.addLikeHandler}
        index={index}
        ideationStatus={ideationStatus}
        hasCurrentUserLiked={hasCurrentUserLiked}
      />
    )
  }

  render (
    { title, topicsFullWidth, topicIds, buttonLabel = '', buttonUrl = '', allProductAreas = [] }: Props,
    { featuredTopics }: State
  ) {
    if (!topicIds || !topicIds.length || !featuredTopics.length) {
      return null
    }
    const externalLink = buttonUrl && (typeof window !== 'undefined' && !buttonUrl.includes(window.location.hostname))
    const isDeviceMobile: boolean = typeof window !== 'undefined' && window.inSidedData?.device === 'mobile'

    return (
      <section class='featured-topics qa-featured-topics-container sitewidth'>
        {title &&
          <h3 class='widget__heading featured-topics__heading'>{title}</h3>}
        <div class={`featured-topics__list ${featuredTopics.length === 1 ? 'featured-topics__list--no-slider' : ''}`}>
          {
            featuredTopics.map((featured, index) => {
              let customClass = ''

              if (!isDeviceMobile && index >= topicsFullWidth && ((featuredTopics.length - topicsFullWidth === 2) || (featuredTopics.length - topicsFullWidth === 4))) {
                customClass = 'featured-topic--half-width'
              }

              if (!isDeviceMobile && index >= topicsFullWidth && (featuredTopics.length - topicsFullWidth === 1)) {
                customClass = 'featured-topic--full-width'
              }
              if ('startsAt' in featured) {
                customClass = 'featured-topic ' + customClass
                customClass = (index < topicsFullWidth) ? 'featured-topic--full-width ' + customClass : customClass

                return this.renderEvent(featured, customClass, index, topicsFullWidth)
              }
              if ('content' in featured) {
                return this.renderProductUpdate(featured, index, topicsFullWidth, customClass, allProductAreas)
              }

              return this.renderTopic(featured, index, topicsFullWidth, customClass)
            }
            )
}
        </div>
        {buttonLabel && buttonUrl &&
          <a
            class='featured-topics__show-more-button'
            href={buttonUrl}
            target={`${externalLink ? '_blank' : '_self'}`}
            rel={`${externalLink ? 'noreferrer nofollow noopener' : ''}`}
          >
            <Button
              title={buttonLabel}
            />
          </a>}
      </section>
    )
  }
}
